import { useNavigate } from '@tanstack/react-router'
import CantileverButton from 'components/CantileverButton'
import { AssetTableAssetViz, WebGLLayer } from 'features/assetDatabase'
import { useGeckoLocale } from 'features/locale'
import {
  AggregateUnitData,
  CardBorderRadius,
  HealthScorePill,
  NavigationButton,
  useSiteViewColors,
} from 'features/overview'
import isNil from 'lodash/isNil'
import React, { FC } from 'react'
import styled from 'styled-components'
import { type CentralizedHealthScore } from 'types'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoSite,
  GeckoUnit,
  RolodexEntryType,
} from 'types'
import { displayDate } from 'utils/dateHelpers'

const UnitSelectionDisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 10rem;
`
const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`
const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`
const TitleText = styled.span`
  height: max-content;
  width: max-content;
  font-size: ${({ theme }) => theme.fontStyles.header.header_h1};
  font-weight: bold;
`
const SubTitleText = styled.span`
  height: max-content;
  width: max-content;
  font-size: ${({ theme }) => theme.fontStyles.caption.regular.value.fontSize};
  color: ${({ theme }) => theme.forecast.text3};
`
const RerouteContainer = styled.div`
  display: flex;
  padding: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.background4};
  border-radius: ${CardBorderRadius};
`
const SubSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const HealthScoreTextContainer = styled.small`
  height: max-content;
  width: max-content;
`
const PointyCornerHealthPill = styled(HealthScorePill)`
  padding: 0.15rem;
  border-radius: ${CardBorderRadius};
`

const LatestInspectionContents = styled.div<{
  $canClick: boolean
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.forecast.background4};
  border-radius: ${CardBorderRadius};

  &:hover {
    cursor: ${({ $canClick }) => ($canClick ? 'pointer' : 'not-allowed')};
  }
`
const AssetImageThumbnail = styled.div`
  height: inherit;
  width: 2rem;

  * {
    object-fit: cover;
    width: 100% !important;
    height: 100% !important;
  }
`

const InspectionDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 0.15rem;
`

export const UnitSelectionDisplay: FC<{
  aggUnitData: AggregateUnitData
  isCalculatingRL: boolean
  site: GeckoSite
  selectedYear: number
}> = (props) => {
  const navigate = useNavigate()
  const subTitleData = [
    props.aggUnitData.unitSpecs.unitData.data.erp_id,
    ...(props.aggUnitData.unitSpecs.unitData.data.groupings?.map(
      (id) => props.site.data.groupings[id]?.name
    ) ?? []),
  ].filter((val) => !isNil(val))

  const componentForRelevantInspection = !isNil(
    props.aggUnitData.mostRecentInspectionData.inspection
  )
    ? props.aggUnitData.unitSpecs.childComponents.find(
        (comp) =>
          comp.id ===
          props.aggUnitData.mostRecentInspectionData.inspection!.tags.component
      )
    : undefined

  return (
    <UnitSelectionDisplayContainer>
      <HeaderSection
        headerText={props.aggUnitData.unitSpecs.unitData.data.name}
        subHeaderText={subTitleData}
        siteID={props.site.id}
        unitID={props.aggUnitData.unitSpecs.unitData.id}
      />
      <HealthScoreSection
        isCalculatingRL={props.isCalculatingRL}
        healthScore={
          props.aggUnitData.health[props.selectedYear].unitHealthScoreForYear
        }
        minRL={props.aggUnitData.health[props.selectedYear]?.minRLFromYear}
        percentBelowTMin={
          props.aggUnitData.health[props.selectedYear]?.percentBelowTMin
        }
        riskScore={props.aggUnitData.health[props.selectedYear]?.riskScore}
      />
      <MostRecentInspectionSection
        siteID={props.site.id}
        unit={props.aggUnitData.unitSpecs.unitData}
        assetModel={props.aggUnitData.unitSpecs.assetModel}
        inspection={props.aggUnitData.mostRecentInspectionData.inspection}
        cdf={props.aggUnitData.mostRecentInspectionData.relevantCDF}
        component={componentForRelevantInspection}
        getColorThresholdForComponent={() =>
          props.aggUnitData.mostRecentInspectionData.relevantColorThreshold
        }
      />
      {props.aggUnitData.unitSpecs.kestrelModelId && (
        <CantileverButton
          primary
          onClick={(e) => {
            e.stopPropagation()
            navigate({
              to: '/systemview/$entryID',
              params: {
                entryID: props.aggUnitData.unitSpecs.unitData.id,
              },
            })
          }}>
          Enhanced View
        </CantileverButton>
      )}
    </UnitSelectionDisplayContainer>
  )
}

// -------------------------------------------------------------------

const HeaderSection: FC<{
  headerText: string
  subHeaderText: ReadonlyArray<string>
  siteID: string
  unitID: string
}> = (props) => {
  return (
    <HeaderContainer>
      <TitleContainer>
        <TitleText>{props.headerText}</TitleText>
        <SubTitleText>{props.subHeaderText.join(' • ')}</SubTitleText>
      </TitleContainer>
      <RerouteContainer>
        <NavigationButton
          to='/assets/$siteID/$unitID'
          params={{
            siteID: props.siteID,
            unitID: props.unitID,
          }}
        />
      </RerouteContainer>
    </HeaderContainer>
  )
}

const HealthScoreSection: FC<{
  isCalculatingRL: boolean
  minRL: number | undefined
  percentBelowTMin: number | undefined
  healthScore: CentralizedHealthScore
  riskScore: number | undefined
}> = (props) => {
  const siteViewColors = useSiteViewColors()

  return (
    <SubSectionContainer>
      <SubTitleText>Remaining Life</SubTitleText>
      <PointyCornerHealthPill
        siteViewColors={siteViewColors}
        healthScoreData={
          props.isCalculatingRL
            ? { isLoading: true }
            : {
                healthScore: props.healthScore,
                minRL: props.minRL,
                percentBelowTMin: props.percentBelowTMin,
                riskScore: props.riskScore,
              }
        }
        textContainer={<HealthScoreTextContainer />}
      />
    </SubSectionContainer>
  )
}

const MostRecentInspectionSection: FC<{
  siteID: string
  unit: GeckoUnit
  inspection: GeckoInspection | undefined
  assetModel: GeckoAssetModel | undefined
  cdf: GeckoComponentDataFrame | undefined
  component: GeckoComponent | undefined
  getColorThresholdForComponent?: (
    cdf: GeckoComponentDataFrame
  ) => GeckoColorScaleThreshold | undefined
}> = (props) => {
  const { locale } = useGeckoLocale()
  const navigate = useNavigate()

  return (
    <SubSectionContainer>
      <SubTitleText>Latest Inspection</SubTitleText>
      {isNil(props.inspection) ? (
        <TitleContainer>N/A</TitleContainer>
      ) : (
        <LatestInspectionContents
          $canClick={!isNil(props.inspection) && !isNil(props.component)}
          onClick={(e) => {
            if (isNil(props.inspection) || isNil(props.component)) return
            e.stopPropagation()
            const searchParam =
              props.inspection.type === RolodexEntryType.VISUAL_INSPECTION
                ? 'visualInspectionId'
                : 'inspectionId'
            navigate({
              to: '/details/$entryID',
              params: {
                entryID: props.component.id,
              },
              search: {
                [searchParam]: props.inspection.id,
              },
            })
          }}>
          {!isNil(props.cdf) && (
            <WebGLLayer>
              <AssetImageThumbnail>
                <AssetTableAssetViz
                  key={props.unit.id}
                  unitID={props.unit.id}
                  siteID={props.siteID}
                  cardProps={{
                    unit: props.unit,
                    assetModel: props.assetModel,
                    componentDataFrame: props.cdf,
                    component: props.component,
                    getColorThresholdForComponent:
                      props.getColorThresholdForComponent,
                  }}
                />
              </AssetImageThumbnail>
            </WebGLLayer>
          )}
          <InspectionDetailsContainer>
            <TitleText>
              {displayDate(props.inspection.data.date, locale)}
            </TitleText>
            <SubTitleText>
              {props.inspection.data.inspection_type?.toUpperCase()}
            </SubTitleText>
          </InspectionDetailsContainer>
        </LatestInspectionContents>
      )}
    </SubSectionContainer>
  )
}
